import React, { useState, useCallback, useMemo, useEffect } from 'react';
import CommonDataGrid from '../../Stateless/CommonDataGrid';
import ErrorHandler from '../../Stateless/Common/ErrorHandler';
import BlockIcon from '@mui/icons-material/Block';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  Card,
  CardContent,
  Alert,
  Button,
  InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { atom, useSetAtom } from 'jotai';
import {
  useAllSubAdmin,
  useDeleteSubAdmin,
  usePatchSubAdmin,
} from '../../Query/AddSubAdminQuery';
import SearchIcon from '@mui/icons-material/Search';
import AddSubAdmin from '../AddSubAdmin';
import EditAddSubAdmin from '../EditSubAdmin';
import AddIcon from '@mui/icons-material/Add';
import { COLORS } from '../../../theme/themeConstants';
import EditStoreOwnerModal from '../EditStoreOwnerModal';
import { useSnackbar } from '../../Stateless/SnackBarProvider';
import { useSubAdminParams } from './useSubAdminParams';
import { useDebounce } from '../Utils/useDounce';
import PermissionIconButton from '../../Stateless/Common/PermissionIconButton';
import PermissionActionButtons from '../../Stateless/Common/PermissionActionButtons';
import { usePermissions } from '../../../context/PermissionContext';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';

export const subAdminAtom = atom<SubAdminData | null>(null);

interface SubAdminData {
  id?: string;
  name?: string;
  email?: string;
  mobileNumber?: string;
  lastLoginDate?: string;
  lastLogoutDate?: string;
  lastLoginIp?: string;
  status?: string;
  privilege?: boolean;
  permissions?: any;
}

// const formatDate = (
//   params: { value: string | undefined | null } | string | undefined | null
// ) => {
//   if (!params) return '';
//   const dateString = typeof params === 'object' ? params.value : params;
//   if (!dateString) return '';
//   try {
//     const date = new Date(dateString);
//     if (isNaN(date.getTime())) return dateString;
//     const month = String(date.getMonth() + 1).padStart(2, '0');
//     const day = String(date.getDate()).padStart(2, '0');
//     const year = date.getFullYear();
//     let hours = date.getHours();
//     // const minutes = String(date.getMinutes()).padStart(2, '0');
//     // const ampm = hours >= 12 ? 'PM' : 'AM';
//     hours = hours % 12;
//     hours = hours ? hours : 12;
//     return `${day}-${month}-${year} ${hours}`;
//   } catch (error) {
//     console.error('Error formatting date:', error);
//     return dateString;
//   }
// };

const formatDate = (
  params: { value: string | undefined | null } | string | undefined | null
) => {
  if (!params) return '';
  const dateString = typeof params === 'object' ? params.value : params;
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' }); // "May", "Jun", etc.
    const year = date.getFullYear();

    // let hours = date.getHours();
    // hours = hours % 12 || 12;

    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

const SubAdminTable = () => {
  const { showMessage } = useSnackbar();


  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  const { params, dispatch } = useSubAdminParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);
  const { data: subAdmin, isLoading } = useAllSubAdmin(memoizedParams);

  const setSubAdminAtom = useSetAtom(subAdminAtom);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const snackbarMessage = ''; // Replace with actual message
  const snackbarSeverity: 'success' | 'error' | 'info' | 'warning' = 'info';
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [modalContent] = useState<'add' | 'edit'>('edit');

  const { mutate: patchSubAdmin } = usePatchSubAdmin();

  const { mutate: deleteSubAdmin } = useDeleteSubAdmin();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);


  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  const handleRowSelect = useCallback((id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  }, []);

  const handleBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one sub-admin to block', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to block?');
      if (!confirmed) return;

      patchSubAdmin({
        id: selectedRows,
        data: { status: 'blocked' },
      });
    }
  };

  const handleUnBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one sub-admin to unblock', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to unblock?');
      if (!confirmed) return;

      patchSubAdmin({
        id: selectedRows,
        data: { status: 'unblocked' },
      });
    }
  };
  const handleBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to block this sub-admin?'
    );
    if (!confirmed) return;

    await patchSubAdmin({
      id: [id],
      data: { status: 'blocked' },
    });
  };
  const handleUnBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to unblock this sub-admin?'
    );
    if (!confirmed) return;

    await patchSubAdmin({
      id: [id],
      data: { status: 'unblocked' },
    });
  };

  const handleDeleteClick = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one sub-admin to delete', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      deleteSubAdmin(selectedRows, {
        onSuccess: () => {
          setSelectedRows([]); // Clear selection after successful deletion
        }
      });
    }
  }, [selectedRows, deleteSubAdmin, showMessage]);

  const handleDeleteButton = useCallback(
    (id: string) => {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      deleteSubAdmin([id], {
        onSuccess: () => {
          setSelectedRows([]); // Clear selection after successful deletion
        }
      });
    },
    [deleteSubAdmin]
  );

  // Compute which bulk actions to show based on selected rows' statuses
  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = subAdmin?.data || [];
    const idToStatus = new Map<string, string>(
      dataList.map((item: any) => [String(item.id), String(item.status || '')])
    );
    const statuses = selectedRows.map((id) => idToStatus.get(String(id)) || '');
    const hasBlocked = statuses.some((s) => s === 'blocked');
    const hasUnblocked = statuses.some((s) => s === 'unblocked');
    return {
      showBlock: hasUnblocked,
      showUnblock: hasBlocked,
    };
  }, [selectedRows, subAdmin]);

  const handleAddSubAdmin = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleEditClick = (row: SubAdminData) => {
    setSubAdminAtom(row);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSubAdminAtom(null);
  };

  // Row actions (Edit/Delete) menu
  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { hasPermission } = usePermissions();
    const canEdit = hasPermission('subAdmin', 'Edit');
    const canDelete = hasPermission('subAdmin', 'Delete');
    if (!canEdit && !canDelete) return null;
    const id = typeof row.id === 'string' ? row.id : '';
    return (
      <>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {canEdit && (
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                handleEditClick(row);
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                if (id) handleDeleteButton(id);
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const MobileView = () => (
    <Box sx={{ mt: 2 }}>
      {subAdmin?.data?.map((row: SubAdminData) => (
        <Card key={row.id} sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Checkbox
                size="small"
                checked={selectedRows.includes(row.id || '')}
                onChange={() => handleRowSelect(row.id || '')}
              />
              <Typography variant="subtitle1" sx={{ ml: 1 }}>
                {row.name}
              </Typography>
            </Box>
            <Box sx={{ pl: 4 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Email:</strong> {row.email}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Mobile No.:</strong> {row.mobileNumber}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Last Login Date:</strong> {row.lastLoginDate}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Last Logout Date:</strong> {row.lastLogoutDate}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Last Login IP:</strong> {row.lastLoginIp}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <PermissionIconButton
                  module="subAdmin"
                  action="edit"
                  onClick={() => handleEditClick(row)}
                  icon={<EditIcon fontSize="small" />}
                  tooltip="Edit"
                />
                <PermissionIconButton
                  module="subAdmin"
                  action="unBlock"
                  onClick={() => row.id && handleUnBlockClickButton(row.id)}
                  icon={<CheckCircleIcon fontSize="small" />}
                  color="success"
                  tooltip="Unblock"
                />
                <PermissionIconButton
                  module="subAdmin"
                  action="delete"
                  onClick={() => row.id && handleDeleteButton(row.id)}
                  icon={<DeleteIcon fontSize="small" />}
                  color="error"
                  tooltip="Delete"
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const { hasPermission } = usePermissions();

  return (
    // <Container maxWidth="xl" sx={{ py: 4 }}>
    // <Paper
    //   elevation={3}
    //   sx={{
    //     p: 2,
    //     borderRadius: 3,
    //     backgroundColor: 'background.paper',
    //     boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    //   }}
    // >
    <Box
      sx={{
        p: 2,
        border: '1px solid #e0e0e0',
        marginTop: '20px',
        color: '#666',
      }}
    >
      {/* <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
          Manage Stores
        </Typography> */}

      {snackbarOpen && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity={snackbarSeverity}
            onClose={() => setSnackbarOpen(false)}
          >
            {snackbarMessage}
          </Alert>
        </Box>
      )}

      <Box
        sx={{
          // p: 2,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2, // Optional: adds spacing between wrapped items
        }}
      >
        {/* Search - Left */}
        <Box
          sx={{
            width: 600,
            border: '1px solid #ccc',
            borderRadius: '8px',
            background: '#fff',
          }}
        >
          <TextField
            fullWidth
            placeholder="Search by Name, Email, or Mobile Number"
            size="medium"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              '.MuiOutlinedInput-root': {
                height: 40,
                width: 600,
                border: 'none', // remove inner border
              },
              '& fieldset': {
                border: 'none', // remove Material-UI default border
              },
              '& .MuiInputBase-input': {
                paddingTop: '10px', // adjust this value to move the placeholder lower
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: '#aaa', mt: '4px' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Action Buttons + Add Button - Right */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <PermissionActionButtons
            module="subAdmin"
            onBlock={showBlock ? handleBlockClick : undefined}
            onUnblock={showUnblock ? handleUnBlockClick : undefined}
            onDelete={selectedRows.length ? handleDeleteClick : undefined}
          />
          {hasPermission('subAdmin', 'Add') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddSubAdmin}
              size="small"
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                bgcolor: COLORS.primary.main,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: COLORS.primary.main,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              Add Sub Admin
            </Button>
          )}
        </Box>
      </Box>
      {/* Add Sub Admin Dialog */}
      <EditStoreOwnerModal
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        title="Add Sub Admin"
      >
        <AddSubAdmin onCancel={handleCloseAddDialog} />
      </EditStoreOwnerModal>

      {/* Edit Sub Admin Dialog */}
      {/* <Dialog
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          maxWidth="sm"
          fullWidth
        >
          <EditAddSubAdmin onCancel={handleCloseAddDialog} />
        </Dialog> */}

      <EditStoreOwnerModal
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        title={modalContent === 'add' ? 'Add Sub Admin' : 'Edit Sub Admin'}
      >
        <EditAddSubAdmin onCancel={handleCloseEditDialog} />
      </EditStoreOwnerModal>

      {isMobile ? (
        <MobileView />
      ) : (
        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={(subAdmin?.data || []).map(
            (row: SubAdminData, index: number) => {
              const formattedRow = {
                ...row,
                lastLoginDate: row.lastLoginDate || '',
                lastLogoutDate: row.lastLogoutDate || '',
                serialNumber: index + 1 + (Number(params.page - 1)) * Number(params.limit),
              };
              return formattedRow;
            }
          )}
          columns={[
            {
              field: 'serialNumber',
              headerName: 'S.No',
              flex: 0.5,
              sortable: true
            },
            { field: 'name', headerName: 'Name', flex: 1 },
            { field: 'email', headerName: 'Email', flex: 1.5, sortable: true, },
            {
              field: 'mobileNumber',
              headerName: 'Mobile No.',
              flex: 1,
            },
            {
              field: 'lastLoginDate',
              headerName: 'Last Login Date',
              flex: 1.2,
              valueFormatter: (params) => formatDate(params),
            },
            {
              field: 'lastLogoutDate',
              headerName: 'Last Logout Date',
              flex: 1.2,
              valueFormatter: (params) => formatDate(params),
            },
            {
              field: 'lastLoginIp',
              headerName: 'Last Login IP',
              flex: 1,
            },
            {
              field: 'status',
              headerName: 'Status',
              flex: 0.5,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              renderCell: (params) => {
                const isBlocked = params.value === 'blocked';
                const id = params.row.id;
                const validId = typeof id === 'string' ? id : '';
                const action = isBlocked ? 'unBlock' : 'block';

                return (
                  <PermissionIconButton
                    module="subAdmin"
                    action={action}
                    onClick={() =>
                      isBlocked
                        ? handleUnBlockClickButton(validId)
                        : handleBlockClickButton(validId)
                    }
                    icon={
                      isBlocked ? (
                        <BlockIcon fontSize="small" />
                      ) : (
                        <CheckCircleIcon fontSize="small" />
                      )
                    }
                    color={isBlocked ? 'error' : 'success'}
                    tooltip={isBlocked ? 'Unblock' : 'Block'}
                  />
                );
              },
            },
            {
              field: 'actions',
              headerName: 'Actions',
              flex: 0.6,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              renderCell: (params: any) => <RowActions row={params.row} />,
            },
          ]}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={subAdmin?.pagination?.totalItems || 0}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message="No sub-admins found" onRetry={() => { }} />
          }}
          onRowSelectionModelChange={(ids: string[]) => setSelectedRows(ids)}
          selectionModel={selectedRows}
        />
      )}

      {/* <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing 1 to 2 of 2 entries
          </Typography>
        </Box> */}
    </Box>
    // </Container>
  );
};

export default SubAdminTable;
